const reqFuelKgToTon = (reqFuel) => {
  return (reqFuel / 1000 + 0.05).toFixed(1) ?? 0;
};

const firstXmlValue = (value) => {
  if (value == null) return "";
  if (Array.isArray(value)) return String(value[0] ?? "");
  return String(value);
};

export const getOfpVersion = (flightPlan) => firstXmlValue(flightPlan?.sFPVer);

export const getOfpTimestamp = (flightPlan) =>
  firstXmlValue(flightPlan?.dtFPTimestamp);

const parseOfpTimestamp = (flightPlan) => {
  const parsed = Date.parse(getOfpTimestamp(flightPlan));
  return Number.isFinite(parsed) ? parsed : null;
};

export const hasOfpVersionChanged = (previousFlightPlan, nextFlightPlan) => {
  const previousTimestamp = parseOfpTimestamp(previousFlightPlan);
  const nextTimestamp = parseOfpTimestamp(nextFlightPlan);
  if (previousTimestamp == null || nextTimestamp == null) return false;
  return nextTimestamp > previousTimestamp;
};

export const formatOfpFuelResetNotice = (fpVer) =>
  `New FPVer: ${fpVer}. Changes to "Requested Fuel" has been reset.`;

const storedReqFuelIsMissing = (storedReqFuel) =>
  storedReqFuel == null || storedReqFuel === "";

export const resolveOfpFuelReset = ({
  previousFlightPlan,
  nextFlightPlan,
  inMemoryFlightPlan,
  storedReqFuel,
  isActiveFlight,
  computedReqFuel,
} = {}) => {
  const ofpVersionChanged =
    hasOfpVersionChanged(previousFlightPlan, nextFlightPlan) ||
    hasOfpVersionChanged(inMemoryFlightPlan, nextFlightPlan);
  const shouldResetReqFuel =
    ofpVersionChanged || storedReqFuelIsMissing(storedReqFuel);
  const nextVersion = getOfpVersion(nextFlightPlan);
  const notice =
    ofpVersionChanged && isActiveFlight && nextVersion
      ? formatOfpFuelResetNotice(nextVersion)
      : null;

  return {
    ofpVersionChanged,
    shouldResetReqFuel,
    notice,
    reqFuel: shouldResetReqFuel ? computedReqFuel : undefined,
  };
};

export const calculateReqFuel = (
  blockFuel,
  firstAlternateAirport,
  selectedAlternateAirport
) => {
  const reqFuelKg =
    (blockFuel || 0) -
    (firstAlternateAirport?.nAlternateFuel_KG || 0) +
    (+selectedAlternateAirport?.nAlternateFuel_KG || 0);
  const reqFuelTon = reqFuelKgToTon(reqFuelKg);
  return reqFuelTon;
};

export const calculateIncreasedTripFuelKg = (extraFuelKg, tripFuelDuration) => {
  // If extra fuel is decreased, skip increase of trip fuel
  if (extraFuelKg < 0) return 0;

  const reqFuelBaseIncreaseProcentage = 3.5; // 3.5%

  const reqFuelCurrentIncreaseProcentage =
    reqFuelBaseIncreaseProcentage * (tripFuelDuration || 0);
  const increasedTripFuelKg = Math.ceil(
    (reqFuelCurrentIncreaseProcentage / 100) * extraFuelKg
  );

  return increasedTripFuelKg;
};

export const isTankeringFuel = (extraFuel) =>
  extraFuel?.sExtra_FuelReason?.includes("TANKERING") ?? false;

export const getTankeringFuelKg = (flightPlan) =>
  Number(
    firstXmlValue(
      flightPlan?.ExtraFuelList?.flatMap((list) => list?.ExtraFuel ?? []).find(
        isTankeringFuel
      )?.nExtra_Fuel_KG
    )
  ) || 0;

// Requested fuel below the OFP block fuel is taken from planned tankering
// first. Contingency is never reduced, so what is left is negative discretionary.
export const distributeFuelReduction = (extraFuelKg, tankeringKg) => {
  const reductionKg = Math.max(-extraFuelKg, 0);
  const tankeringReductionKg = Math.min(reductionKg, Math.max(tankeringKg, 0));

  return {
    tankeringReductionKg,
    discretionaryKg: extraFuelKg + tankeringReductionKg,
  };
};
